import { defineStore } from 'pinia'
import { ref } from 'vue'
import MiiaFwConfig from '@/configs/config'
import {
  exportRequest,
  exportStatus,
  downloadUrl
} from '@/api/emergencyResultCsv/emergencyResultCsvApiGwFuncs'
import { ERROR_CODES } from '@/constants/error/errorCode'
import { GlobalError } from '@/utils/globalError/globalError'
import type { ExportRequestParam } from '@/types/api/emergencyResultCsv/exportRequestType'
import type { ExportStatusParam } from '@/types/api/emergencyResultCsv/exportStatusType'
import { ExportProcessState } from '@/types/api/emergencyResultCsv/exportStatusType'
import type { DownloadUrlParam } from '@/types/api/emergencyResultCsv/downloadUrlType'
import { sleep } from '@/utils/timeUtil'
import { downloadFromkey } from '@/utils/fileUtil'
import { jaFormatDate } from '@/utils/dateUtil/dateUtils'

/** 応急危険度調査結果CSV Store */
export const useEmergencyResultCsvStore = defineStore('emergencyResultCsv', () => {
  /** CSV出力 ジョブID */
  const exportProcessStatus = ref<ExportProcessState | null>(null)

  /**
   * CSV出力を実行する
   * @param govCode 自治体コード
   * @param disasterId 災害ID
   */
  const exportCsv = async (govCode: string, disasterId: string): Promise<void> => {
    // フロント内のステータス管理の便宜上、開始時点で「待機」に設定する
    exportProcessStatus.value = ExportProcessState.WAITING
    // ポーリング ⇒ URL取得 ⇒ ダウンロード
    const fileName = jaFormatDate(new Date(), '応急危険度判定結果_YYYY-MM-DD.csv')
    await requestExportCsv(govCode, disasterId)
      .then(async jobId => await pollingExportStatus(jobId))
      .then(async jobId => await getDownloadKey(jobId))
      .then(async key => await downloadFromkey(key, fileName))
      .catch(e => {
        exportProcessStatus.value = ExportProcessState.ERROR
        throw e
      })
  }

  /** CSV出力リクエストを実行する */
  const requestExportCsv = async (govCode: string, disasterId: string): Promise<string> => {
    const param: ExportRequestParam = {
      system: MiiaFwConfig.config.sysId,
      govCode,
      disasterId
    }
    const res = await exportRequest(param)
    if (res.error || res.result == null) {
      console.error(res.error)
      throw new GlobalError([ERROR_CODES.CSV_CREATION_FAILED])
    }
    return res.result.jobId
  }

  /** CSVの出力状態をポーリングで監視する */
  const pollingExportStatus = async (jobId: string): Promise<string> => {
    const param: ExportStatusParam = {
      system: MiiaFwConfig.config.sysId,
      jobId
    }
    // ポーリングで監視
    while (true) {
      const res = await exportStatus(param)
      if (res.error || res.result == null) {
        console.error(res.error)
        throw new GlobalError([ERROR_CODES.CSV_CREATION_FAILED])
      }
      exportProcessStatus.value = res.result.processState
      if (exportProcessStatus.value === ExportProcessState.ERROR) {
        // CSV作成におけるバックエンドのエラー
        throw new GlobalError([ERROR_CODES.CSV_CREATION_FAILED])
      }
      if (exportProcessStatus.value === ExportProcessState.COMPLETED) {
        break
      }
      // 1秒待機
      await sleep(1000)
    }

    return jobId
  }

  /** ダウンロードURLを取得する */
  const getDownloadKey = async (jobId: string) => {
    const param: DownloadUrlParam = {
      system: MiiaFwConfig.config.sysId,
      jobId
    }
    const res = await downloadUrl(param)
    if (res.error || res.result == null) {
      console.error(res.error)
      throw new GlobalError([ERROR_CODES.CSV_CREATION_FAILED])
    }
    // URLオブジェクトを作成
    const url = new URL(res.result.presignedUrl)
    // パス部分を取得
    const pathname = url.pathname
    // 先頭のスラッシュ除去
    const key = pathname.startsWith('/') ? pathname.slice(1) : pathname
    return key
  }

  return {
    exportProcessStatus,
    exportCsv
  }
})
